import React, { useCallback, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGetSuivisForSportive, apiUpdateSuivi } from '../services/api';
import { UI } from '../constants/colors';
import { parseISODate, toISODate, todayISO } from '../utils/formDate';
import './FormShare.css';

import { ReactComponent as ShareB } from '../assets/forms/form_emojis/shareb.svg';
import { ReactComponent as InfoSG } from '../assets/forms/form_emojis/infosg.svg';
import { ReactComponent as CalendarG } from '../assets/forms/form_emojis/calendarg.svg';
import { ReactComponent as DropletY } from '../assets/forms/form_emojis/droplety.svg';
import { ReactComponent as HeartR } from '../assets/forms/form_emojis/heartr.svg';
import { ReactComponent as HeartbeatB } from '../assets/forms/form_emojis/heartbeatb.svg';

/** Il y a `months` mois, au format `YYYY-MM-DD` local. */
const monthsAgoISO = (months) => {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return toISODate(d);
};

// Raccourcis de fenêtre : ils ne fixent que le début, la fin reste ouverte (le coach
// continue de voir les nouvelles saisies). « Personnalisée » ouvre les deux bornes.
const PERIODS = [
  { key: 'all', label: 'Tout mon historique' },
  { key: '3m', label: '3 derniers mois', months: 3 },
  { key: '6m', label: '6 derniers mois', months: 6 },
];

const coachName = (s) =>
  [s.coach_prenom, s.coach_nom].filter(Boolean).join(' ') || `Coach #${s.id_coach}`;

const fmtISO = (iso) => parseISODate(iso)?.toLocaleDateString('fr-FR') ?? '—';

const FormShare = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getToken } = useAuth();

  const [suivis, setSuivis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Rien n'est coché d'avance : l'écran reflète les partages réellement en vigueur.
  // Une case pré-cochée ne vaut pas consentement (RGPD Art. 4-11), et repartir d'un
  // choix par défaut rouvrait d'un tap un partage que la sportive avait retiré.
  const [selectedData, setSelectedData] = useState([]);
  const [withDetail, setWithDetail] = useState(false);
  // Fenêtre de données visible par le coach, bornes incluses ; '' = sans borne.
  const [period, setPeriod] = useState('all');
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  // Suivi en cours de réglage : chaque coach a ses propres partages.
  const [selectedId, setSelectedId] = useState(null);
  const current = suivis.find((s) => s.id === selectedId) || null;

  // Recharge l'écran sur les partages réellement en vigueur pour ce coach.
  const loadSuivi = useCallback((s) => {
    setSelectedId(s.id);
    setSelectedData(
      [
        s.partage_cycle && 'cycle',
        s.partage_mental && 'mental',
        s.partage_sport && 'sport',
      ].filter(Boolean),
    );
    setWithDetail(!!s.partage_detail);
    // Une fenêtre déjà fixée s'affiche telle quelle, en « Personnalisée » : on ne
    // devine pas quel raccourci l'a produite.
    const hasWindow = Boolean(s.partage_debut || s.partage_fin);
    setPeriod(hasWindow ? 'custom' : 'all');
    setWindowStart(s.partage_debut || '');
    setWindowEnd(s.partage_fin || '');
  }, []);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    apiGetSuivisForSportive(user.id, getToken())
      .then((list) => {
        setSuivis(list);
        if (list[0]) loadSuivi(list[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user?.id, getToken, loadSuivi]);

  const toggleData = (data) => {
    setSelectedData((prev) =>
      prev.includes(data) ? prev.filter((d) => d !== data) : [...prev, data]
    );
  };

  const choosePeriod = (key) => {
    setPeriod(key);
    const preset = PERIODS.find((p) => p.key === key);
    if (preset) {
      setWindowStart(preset.months ? monthsAgoISO(preset.months) : '');
      setWindowEnd('');
    }
  };

  const windowInvalid = Boolean(windowStart && windowEnd && windowStart > windowEnd);

  const windowSummary = () => {
    if (!windowStart && !windowEnd) return 'Tout ton historique';
    if (!windowEnd) return `Depuis le ${fmtISO(windowStart)}`;
    if (!windowStart) return `Jusqu'au ${fmtISO(windowEnd)}`;
    return `Du ${fmtISO(windowStart)} au ${fmtISO(windowEnd)}`;
  };

  const handleShare = async () => {
    if (!current || windowInvalid) return;
    setError('');
    setSubmitting(true);
    try {
      await apiUpdateSuivi(current.id, {
        partage_cycle: selectedData.includes('cycle'),
        partage_sport: selectedData.includes('sport'),
        partage_mental: selectedData.includes('mental'),
        partage_detail: withDetail,
        partage_debut: windowStart || null,
        partage_fin: windowEnd || null,
      }, getToken());
      navigate('/accueil');
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/formulaire/recap', { state: location.state });
  };

  if (loading) {
    return (
      <div className="form-share">
        <div className="form-share__overlay" onClick={handleCancel} />
        <div className="form-share__modal">
          <p style={{ textAlign: 'center', padding: '2rem' }}>Chargement…</p>
        </div>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="form-share">
        <div className="form-share__overlay" onClick={handleCancel} />
        <div className="form-share__modal">
          <button className="form-share__close" onClick={handleCancel} aria-label="Fermer">✕</button>
          <div className="form-share__header">
            <ShareB className="form-share__header-icon" />
            <h1 className="form-share__title">Partager avec mon coach</h1>
          </div>
          <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '2rem 1rem' }}>
            Aucun coach ne te suit actuellement.<br />
            Demande à ton coach de créer un suivi avec toi.
          </p>
          <div className="form-share__actions">
            <button className="form-share__cancel" onClick={() => navigate('/accueil')}>
              Retourner à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-share">
      <div className="form-share__overlay" onClick={handleCancel} />

      <div className="form-share__modal">
        {/* Close button */}
        <button className="form-share__close" onClick={handleCancel} aria-label="Fermer">✕</button>

        {/* Header */}
        <div className="form-share__header">
          <ShareB className="form-share__header-icon" />
          <h1 className="form-share__title">Partager avec mon coach</h1>
        </div>

        <p className="form-share__subtitle">
          Sélectionne les données que tu souhaites partager avec ton coach
        </p>

        {/* Coach concerné : avec plusieurs coachs, chacun a ses propres partages */}
        <div className="form-share__section">
          <div className="form-share__section-header">
            <InfoSG className="form-share__section-icon" />
            <h2 className="form-share__section-title">{suivis.length > 1 ? 'Quel coach ?' : 'Coach associé'}</h2>
          </div>
          {suivis.length > 1 ? (
            <div className="form-share__period-options">
              {suivis.map((s) => (
                <button
                  key={s.id}
                  className={`form-share__period-option${s.id === current.id ? ' form-share__period-option--selected' : ''}`}
                  onClick={() => loadSuivi(s)}
                >
                  {coachName(s)}
                </button>
              ))}
            </div>
          ) : null}
          <p style={{ fontSize: '0.85rem', color: UI.textLabel, margin: '0.5rem 0' }}>
            {coachName(current)} — suivi depuis le {fmtISO(current.date_debut)}
          </p>
        </div>

        {/* Quelles données ? */}
        <div className="form-share__section">
          <div className="form-share__section-header">
            <InfoSG className="form-share__section-icon" />
            <h2 className="form-share__section-title">Quelles données ?</h2>
          </div>

          <div className="form-share__data-options">
            <button
              className={`form-share__data-option form-share__data-option--cycle${selectedData.includes('cycle') ? ' form-share__data-option--selected' : ''}`}
              onClick={() => toggleData('cycle')}
            >
              <div className="form-share__data-option-icons">
                <DropletY className="form-share__data-option-icon" />
              </div>
              <div className="form-share__data-option-content">
                <span className="form-share__data-option-label">Cycle menstruel</span>
                <span className="form-share__data-option-sub">Flux, symptômes, ovulation</span>
              </div>
            </button>

            <button
              className={`form-share__data-option form-share__data-option--mental${selectedData.includes('mental') ? ' form-share__data-option--selected' : ''}`}
              onClick={() => toggleData('mental')}
            >
              <div className="form-share__data-option-icons">
                <HeartR className="form-share__data-option-icon" />
              </div>
              <div className="form-share__data-option-content">
                <span className="form-share__data-option-label">Mental</span>
                <span className="form-share__data-option-sub">Humeur, énergie, concentration</span>
              </div>
            </button>

            <button
              className={`form-share__data-option form-share__data-option--sport${selectedData.includes('sport') ? ' form-share__data-option--selected' : ''}`}
              onClick={() => toggleData('sport')}
            >
              <div className="form-share__data-option-icons">
                <HeartbeatB className="form-share__data-option-icon" />
              </div>
              <div className="form-share__data-option-content">
                <span className="form-share__data-option-label">Activité sportive</span>
                <span className="form-share__data-option-sub">RPE, fréquence cardiaque</span>
              </div>
            </button>
          </div>
        </div>

        {/* Quelle période ? */}
        <div className="form-share__section">
          <div className="form-share__section-header">
            <CalendarG className="form-share__section-icon" />
            <h2 className="form-share__section-title">Quelle période ?</h2>
          </div>

          <div className="form-share__period-options">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                className={`form-share__period-option${period === p.key ? ' form-share__period-option--selected' : ''}`}
                onClick={() => choosePeriod(p.key)}
              >
                {p.label}
              </button>
            ))}
            <button
              className={`form-share__period-option${period === 'custom' ? ' form-share__period-option--selected' : ''}`}
              onClick={() => setPeriod('custom')}
            >
              Personnalisée
            </button>
          </div>

          {period === 'custom' && (
            <div className="form-share__period-dates">
              <label className="form-share__period-date">
                Du
                <input
                  type="date"
                  value={windowStart}
                  max={windowEnd || todayISO()}
                  onChange={(e) => setWindowStart(e.target.value)}
                />
              </label>
              <label className="form-share__period-date">
                Au
                <input
                  type="date"
                  value={windowEnd}
                  min={windowStart || undefined}
                  onChange={(e) => setWindowEnd(e.target.value)}
                />
              </label>
            </div>
          )}
          <p className="form-share__period-hint">
            {windowInvalid
              ? 'La date de début doit précéder la date de fin.'
              : 'Ton coach ne verra que les données de cette période. Sans date de fin, tes prochaines saisies restent partagées.'}
          </p>
        </div>

        {/* Niveau de détail */}
        <div className="form-share__section">
          <div className="form-share__section-header">
            <InfoSG className="form-share__section-icon" />
            <h2 className="form-share__section-title">Niveau de détail</h2>
          </div>

          <div className="form-share__detail-options">
            <button
              className={`form-share__detail-option${!withDetail ? ' form-share__detail-option--selected' : ''}`}
              onClick={() => setWithDetail(false)}
            >
              <span className="form-share__detail-option-radio" />
              <div className="form-share__detail-option-content">
                <span className="form-share__detail-option-label">Sans détails</span>
                <span className="form-share__detail-option-sub">Le coach voit uniquement les statistiques agrégées</span>
              </div>
            </button>

            <button
              className={`form-share__detail-option${withDetail ? ' form-share__detail-option--selected' : ''}`}
              onClick={() => setWithDetail(true)}
            >
              <span className="form-share__detail-option-radio" />
              <div className="form-share__detail-option-content">
                <span className="form-share__detail-option-label">Avec détails</span>
                <span className="form-share__detail-option-sub">Le coach voit aussi tes notes et ressentis personnels</span>
              </div>
            </button>
          </div>
        </div>

        {error && (
          <p style={{ color: 'var(--danger)', textAlign: 'center', fontSize: '0.85rem', margin: '0.5rem 0' }}>{error}</p>
        )}

        {/* Action buttons */}
        <div className="form-share__actions">
          <button className="form-share__cancel" onClick={handleCancel}>
            Annuler
          </button>
          <button className="form-share__submit" onClick={handleShare} disabled={submitting || windowInvalid}>
            <ShareB className="form-share__submit-icon" />
            {submitting ? 'Enregistrement…' : selectedData.length ? 'Partager' : 'Ne rien partager'}
          </button>
        </div>

        {/* Résumé */}
        <div className="form-share__summary">
          <h3 className="form-share__summary-title">RÉSUMÉ</h3>
          <p className="form-share__summary-text">
            <strong>Données :</strong> {selectedData.map(d => ({ cycle: 'Cycle', mental: 'Mental', sport: 'Sport' }[d])).join(', ') || 'Aucune'}
          </p>
          <p className="form-share__summary-text">
            <strong>Niveau :</strong> {withDetail ? 'Avec détails' : 'Sans détails'}
          </p>
          <p className="form-share__summary-text">
            <strong>Période :</strong> {windowSummary()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FormShare;
